import axios from 'axios'
import { apiBaseUrl, releaseTenantForTab } from '@/utils/tenant'
import { toastBus } from '@/utils/toastBus'
import router from '@/router'
import { useAuthStore } from '@/stores/auth'
import { getAuthToken } from '@/utils/authToken'

/**
 * Clear the session and bounce the user to login after an auth failure.
 * Imports are resolved lazily (inside this function) so the request that
 * triggered the 401 doesn't matter for module init order.
 */
function handleUnauthorized() {
  const authStore = useAuthStore()
  authStore.clearSession()
  // An AdminHub session ran out: its tab back on the browser's tenant, loaded anew there
  if (releaseTenantForTab()) {
    window.location.assign(`${import.meta.env.BASE_URL}login`)
    return
  }
  if (router.currentRoute.value.name !== 'login') {
    router.push({ name: 'login' })
  }
}

declare module 'axios' {
  export interface AxiosRequestConfig {
    /**
     * Opt this request out of the global error toast below. For lookups whose
     * failure the caller handles itself and that carry no meaning for the user
     * — an optional feature that is simply unavailable, not a broken app.
     */
    skipErrorToast?: boolean
  }
}

/**
 * The backend assumes German time when it cannot determine the caller's zone,
 * so mirror that here rather than inventing a different default.
 */
const FALLBACK_TIME_ZONE = 'Europe/Berlin'

/**
 * The viewer's IANA timezone, e.g. "Asia/Bangkok". `resolvedOptions().timeZone`
 * is well supported but can be empty on older engines, and the whole call can
 * throw in locked-down environments — both fall back rather than send nothing.
 */
function resolveTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || FALLBACK_TIME_ZONE
  } catch {
    return FALLBACK_TIME_ZONE
  }
}

const httpClient = axios.create({
  // The chosen Work4All tenant's backend on dev / QA (see `utils/tenant`)
  baseURL: apiBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
  // Requests that reach Work4All can take a long time, some over a minute;
  // uploads set their own, longer timeout.
  timeout: 80_000,
  withCredentials: true,
})

httpClient.interceptors.request.use((config) => {
  const token = getAuthToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  // Tell the backend which language to localize responses in (e.g. error
  // messages, which the response interceptor below surfaces as toasts).
  // Mirrors the i18n locale persisted by the language switcher; defaults to 'en'.
  const lang = localStorage.getItem('LANG')
  config.headers['Accept-Language'] = lang === 'en' || lang === 'de' ? lang : 'en'
  // Tell the backend which timezone the wall-clock times in this request were
  // entered in — and which zone to return them in. Planning setup/dismantling
  // times are plain times with no offset of their own, so without this the
  // backend cannot tell 08:00 in Bangkok from 08:00 in Berlin.
  config.headers['X-Timezone'] = resolveTimeZone()
  return config
})

httpClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // A 401 on anything other than the login attempt itself means the session
    // is no longer valid — clear it and redirect to login.
    const isLoginRequest = (error.config?.url ?? '').includes('/auth/login')
    if (error.response?.status === 401 && !isLoginRequest) {
      handleUnauthorized()
    }

    // Validation errors (e.g. 422) come back as `{ errors: [{ message, field, ... }] }`.
    // Surface those specific messages instead of the generic "status 422".
    const data = error.response?.data
    const validationMessages = Array.isArray(data?.errors)
      ? data.errors
          .map((e: { message?: string }) => e?.message)
          .filter((m: unknown): m is string => typeof m === 'string' && m.length > 0)
      : []

    const message =
      data?.message ||
      data?.error ||
      (validationMessages.length ? validationMessages.join('\n') : '') ||
      error.message ||
      'An unexpected error occurred'

    // LoginView surfaces its own, more specific error messages for failed
    // login attempts — toasting here as well would show the error twice.
    if (!isLoginRequest && !error.config?.skipErrorToast) {
      toastBus.emit({ severity: 'error', summary: message })
    }

    return Promise.reject(error)
  },
)

export default httpClient
